// src/views/Clientes/ActualizacionLimiteDeCredito/ActualizacionLimiteCredito.jsx
import React, { useEffect, useState } from "react";
import { FaHistory, FaEye, FaTrashAlt } from "react-icons/fa";
import useEscClose from "../../useEscClose";
import FiltroLimite from "./FiltroLimite";
import AprobarRechazar from "./AprobarRechazar";
import VerLimite from "./VerLimite";
import CrearSolicitudLimite from "./CrearSolicitudLimite";
import EliminarLimiteCredito from "./EliminarLimiteCredito";
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import { getUsuario } from "../../../utils/auth";
import { shouldRunOnce } from "../../../utils/devOnce";
import { formatoPy } from "../../../utils/formatoNumeros";
import { hasScope } from "../../../utils/auth";

const DEFAULT_PAGE_SIZE = 30;

// Mapeo de estados a IDs (para filtros)
const mapEstado = (valor) => {
  if (!valor) return null;
  if (valor === "SOLICITADA") return 2;
  if (valor === "APROBADA") return 3;
  if (valor === "RECHAZADA") return 4;
  return null;
};
 
export default function ActualizacionLimiteCredito() {
  // Scopes
  const canList = hasScope("actualización límites de crédito:list");
  const canView = hasScope("actualización límites de crédito:view");
  const canCreate = hasScope("actualización límites de crédito:insert");
  const canUpdate = hasScope("actualización límites de crédito:update");
  const canDelete = hasScope("actualización límites de crédito:delete");
  const canAprobarRechazar = hasScope("actualización límites de crédito:aprobar_rechazar");
 
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
 
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
 
  // Filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtros, setFiltros] = useState({});
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);
 
  // Ver detalle
  const [detalleId, setDetalleId] = useState(null);
  useEscClose(() => setDetalleId(null), detalleId != null);
 
  // Aprobar/Rechazar
  const [showEdit, setShowEdit] = useState(false);
  const [codigoEdit, setCodigoEdit] = useState(null);
  useEscClose(() => setShowEdit(false), showEdit);
 
  // Eliminar
  const [showEliminar, setShowEliminar] = useState(false);
  const [eliminarData, setEliminarData] = useState(null);

  // Crear solicitud (ahora modal)
  const [showCrear, setShowCrear] = useState(false);

  // Build params para el backend
  const buildParams = (opt = {}) => {
    const p = new URLSearchParams();
    if (opt.page !== undefined) p.append("page", opt.page);
    if (opt.size !== undefined) p.append("size", opt.size);
    Object.entries(opt).forEach(([key, val]) => {
      if (key === "page" || key === "size") return;
      if (val !== null && val !== "" && val !== undefined) {
        p.append(key, val);
      }
    });
    return p;
  };

  // Fetch del listado
  const fetchList = async (
    pageIndex = paginaActual - 1,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);
    try {
      const params = buildParams({ page: pageIndex, size, ...filtrosAplicados });
      const data = await appFetchJson(
        `${apiRoutes.limiteCredito.list}?${params.toString()}`
      );
      const items = data?.items || data?.content || [];
      const total = data?.totalElements ?? data?.total ?? items.length;
      setSolicitudes(items);
      setTotalBackend(total);
    } catch (err) {
      console.error("Error al obtener solicitudes:", err);
      setSolicitudes([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };
 
  useEffect(() => {
    if (!shouldRunOnce("ActualizacionLimiteCredito:init")) return;
    fetchList(0, porPagina, filtros);
  }, []);

  // Definición de columnas para DynamicTable
  const columns = [
    {
      header: "Cliente",
      accessor: "clienteNombre",
      type: "text",
      width: "12%",
      cellClass: "whitespace-normal break-words leading-tight font-medium",
    },
    {
      header: "Monto solicitado",
      accessor: "valorMontoSolicitado",
      type: "center",
      width: "9%",
      render: (row) => formatoPy(row.valorMontoSolicitado),
    },
    {
      header: "Fecha solicitud",
      accessor: "dataFechaSolicitud",
      type: "secondary",
      width: "9%",
    },
    {
      header: "Monto aprobado",
      accessor: "valorMontoAprobado",
      type: "center",
      width: "9%",
      render: (row) => formatoPy(row.valorMontoAprobado),
    },
    {
      header: "Fecha aprobación",
      accessor: "dataFechaAprobacion",
      type: "secondary",
      width: "9%",
    },
{
  header: "Estado",
  accessor: "estadoNombre",
  type: "status",
  width: "10%",
  render: (row) => {
    const estado = row.estadoNombre?.toUpperCase() || "";
    let colorClass = "text-gray-600";
    if (estado.includes("APROBADA")) colorClass = "text-green-600";
    else if (estado.includes("RECHAZADA")) colorClass = "text-red-600";
    else if (estado.includes("PENDIENTE")) colorClass = "text-sky-500"; // celeste
    return <span className={`font-bold ${colorClass}`}>{row.estadoNombre}</span>;
  },
},
    {
      header: "Usuario solicitud",
      accessor: "codigoUsuarioSolicitud",
      type: "secondary",
      width: "9%",
    },
    {
      header: "Usuario aprobación",
      accessor: "codigoUsuarioAprobacion",
      type: "secondary",
      width: "9%",
    },
    {
      header: "Límite anterior",
      accessor: "valorLimiteAnterior",
      type: "center",
      width: "9%",
      render: (row) => formatoPy(row.valorLimiteAnterior),
    },
    {
      header: "Acción",
      type: "action",
      width: "7%",
      render: (row) => (
        <div className="flex justify-center items-center gap-1">
          {canView && (
            <button
              className="text-gray-500 hover:text-blue-600 p-1"
              title="Visualizar"
              onClick={() => setDetalleId(row.id)}
            >
              <FaEye />
            </button>
          )}
          {canAprobarRechazar && (
            <button
              className="text-gray-500 hover:text-green-600 p-1"
              title="Aprobar / Rechazar"
              onClick={() => {
                setCodigoEdit(row.id);
                setShowEdit(true);
              }}
            >
              <FaHistory />
            </button>
          )}
          {canDelete && (
            <button
              className="text-gray-500 hover:text-red-600 p-1"
              title="Eliminar"
              onClick={() => {
                setEliminarData({
                  id: row.id,
                  clienteNombre: row.clienteNombre,
                });
                setShowEliminar(true);
              }}
            >
              <FaTrashAlt />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Color de borde opcional para tarjetas móviles (no lo usaremos, pero se podría)
  // const getRowColor = (row) => ... (innecesario, dejamos undefined)

  return (
    <div className="page">
      {/* Cabecera con herramientas */}
      <ListHeader
        title="Actualización de Límite de Crédito"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={() => {
          setFiltros({});
          setPaginaActual(1);
          fetchList(0, porPagina, {});
        }}
        showAdd={canCreate}
        addLabel="Crear nueva solicitud"
        onAddClick={() => setShowCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
          fetchList(0, size, filtros);
        }}
        pageSizeOptions={[30, 40, 50]}
      />
 
      {/* Filtros */}
      {mostrarFiltros && (
        <FiltroLimite
          initialFilters={filtros}
          onSubmit={(items, f) => {
            const snap = {
              clienteId: f.clienteId || null,
              clienteNombre: f.clienteNombre || null,
              codigoUsuarioSolicitud: f.usuarioSolicitud || null,
              codigoUsuarioAprobacion: f.usuarioAprobacion || null,
              dataFechaSolicitud: f.fechaSolicitud || null,
              dataFechaAprobacion: f.fechaAprobacion || null,
              estadoId: mapEstado(f.estado),
            };
            setFiltros(snap);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchList(0, porPagina, snap);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* Tabla adaptable */}
      <DynamicTable size="L" data={solicitudes} columns={columns} />

      {/* Pie con paginador */}
      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={(p) => {
          setPaginaActual(p);
          fetchList(p - 1, porPagina, filtros);
        }}
      />
 
      {/* Modales */}
      {detalleId != null && (
        <div className="modal-backdrop">
          <div className="modal-panel-lg">
            <button onClick={() => setDetalleId(null)} className="modal-close-x">
              &times;
            </button>
            <VerLimite id={detalleId} onClose={() => setDetalleId(null)} />
          </div>
        </div>
      )}
 
      {showEdit && (
        <AprobarRechazar
          codigo={codigoEdit}
          onClose={() => setShowEdit(false)}
          onResolved={() => {
            setShowEdit(false);
            fetchList(paginaActual - 1, porPagina, filtros);
          }}
        />
      )}
 
      {showEliminar && eliminarData && (
        <div className="modal-backdrop">
          <div className="modal-panel-lg">
            <button
              onClick={() => {
                setShowEliminar(false);
                setEliminarData(null);
              }}
              className="modal-close-x"
            >
              &times;
            </button>
            <EliminarLimiteCredito
              initial={eliminarData}
              onVolver={() => {
                setShowEliminar(false);
                setEliminarData(null);
              }}
              onEliminar={async () => {
                setShowEliminar(false);
                setEliminarData(null);
                await fetchList(paginaActual - 1, porPagina, filtros);
              }}
            />
          </div>
        </div>
      )}

      {showCrear && (
        <div className="modal-backdrop">
          <div className="modal-panel-lg">
            <button
              onClick={() => setShowCrear(false)}
              className="modal-close-x"
            >
              &times;
            </button>
            <CrearSolicitudLimite
              onBack={() => setShowCrear(false)}
              onGuardar={() => {
                setShowCrear(false);
                fetchList(paginaActual - 1, porPagina, filtros);
              }}
              initialData={{
                ultimoMontoAprobado: "",
                totalLimiteTarjetas: "",
                fechaSolicitud: new Date().toLocaleDateString("es-PY"),
                estado: "SOLICITUD PENDIENTE",
                usuarioSolicitud: getUsuario() || "",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}